import { gql } from "@apollo/client";

// Tutor subject queries and mutations used by SubjectTutorContent.

export const SUBJECT_QUERY = gql`
  query Subject($id: ID!) {
    subject(id: $id) {
      id
      title
    }
  }
`;

export const SUBJECT_DETAIL_BLOCKS_QUERY = gql`
  query SubjectDetailBlocks($subjectId: ID!) {
    subjectDetailBlocks(subjectId: $subjectId) {
      id
      position
      html
    }
  }
`;

export const LESSON_CONTENTS_QUERY = gql`
  query LessonContentsByLesson($lessonId: ID!) {
    lessonContentsByLesson(lessonId: $lessonId) {
      id
      contentType
      contentData
      position
      aiEnabled
    }
  }
`;

export const LESSON_QUIZZES_QUERY = gql`
  query QuizzesByLesson($lessonId: ID!) {
    quizzesByLesson(lessonId: $lessonId) {
      id
      lessonId
      question
      questionType
      position
      isKlausurRelevant
      requiredForCompletion
      difficulty
      sourceType
      explanation
      hint
      createdAt
      updatedAt
    }
  }
`;

export const QUIZ_OPTIONS_QUERY = gql`
  query QuizOptionsByQuiz($quizId: ID!) {
    quizOptionsByQuiz(quizId: $quizId) {
      id
      quizId
      optionText
      isCorrect
      position
      createdAt
      updatedAt
    }
  }
`;

export const LESSON_TASKS_QUERY = gql`
  query LessonTasks($lessonID: ID!) {
    lessonTasks(lessonID: $lessonID) {
      id
      lessonID
      title
      taskText
      hintText
      answerText
      position
      difficulty
      estimatedMinutes
      sourceType
      isPublished
      createdAt
      updatedAt
    }
  }
`;

export const CREATE_SUBJECT_DETAIL_BLOCK_MUTATION = gql`
  mutation CreateSubjectDetailBlock($input: CreateSubjectDetailBlockInput!) {
    createSubjectDetailBlock(input: $input) {
      id
      position
      html
    }
  }
`;

export const UPDATE_SUBJECT_DETAIL_BLOCK_MUTATION = gql`
  mutation UpdateSubjectDetailBlock($input: UpdateSubjectDetailBlockInput!) {
    updateSubjectDetailBlock(input: $input) {
      id
      position
      html
    }
  }
`;

export const DELETE_SUBJECT_DETAIL_BLOCK_MUTATION = gql`
  mutation DeleteSubjectDetailBlock($id: ID!) {
    deleteSubjectDetailBlock(id: $id)
  }
`;

export const CREATE_LESSON_CONTENT_MUTATION = gql`
  mutation CreateLessonContent($input: CreateLessonContentInput!) {
    createLessonContent(input: $input) {
      id
      position
      aiEnabled
      contentType
      contentData
    }
  }
`;

export const UPDATE_LESSON_CONTENT_MUTATION = gql`
  mutation UpdateLessonContent($input: UpdateLessonContentInput!) {
    updateLessonContent(input: $input) {
      id
      position
      aiEnabled
      contentType
      contentData
    }
  }
`;

export const DELETE_LESSON_CONTENT_MUTATION = gql`
  mutation DeleteLessonContent($id: ID!) {
    deleteLessonContent(id: $id)
  }
`;

export const CREATE_QUIZ_MUTATION = gql`
  mutation CreateQuiz($input: CreateQuizInput!) {
    createQuiz(input: $input) {
      id
      lessonId
      question
      questionType
      position
      isKlausurRelevant
      requiredForCompletion
      difficulty
      sourceType
      explanation
      hint
    }
  }
`;

export const UPDATE_QUIZ_MUTATION = gql`
  mutation UpdateQuiz($input: UpdateQuizInput!) {
    updateQuiz(input: $input) {
      id
      lessonId
      question
      questionType
      position
      isKlausurRelevant
      requiredForCompletion
      difficulty
      sourceType
      explanation
      hint
    }
  }
`;

export const DELETE_QUIZ_MUTATION = gql`
  mutation DeleteQuiz($id: ID!) {
    deleteQuiz(id: $id)
  }
`;

export const CREATE_QUIZ_OPTION_MUTATION = gql`
  mutation CreateQuizOption($input: CreateQuizOptionInput!) {
    createQuizOption(input: $input) {
      id
      quizId
      optionText
      isCorrect
      position
    }
  }
`;

export const UPDATE_QUIZ_OPTION_MUTATION = gql`
  mutation UpdateQuizOption($input: UpdateQuizOptionInput!) {
    updateQuizOption(input: $input) {
      id
      quizId
      optionText
      isCorrect
      position
    }
  }
`;

export const DELETE_QUIZ_OPTION_MUTATION = gql`
  mutation DeleteQuizOption($id: ID!) {
    deleteQuizOption(id: $id)
  }
`;

export const CREATE_LESSON_TASK_MUTATION = gql`
  mutation CreateLessonTask($input: CreateLessonTaskInput!) {
    createLessonTask(input: $input) {
      id
      lessonID
      title
      taskText
      hintText
      answerText
      position
      difficulty
      estimatedMinutes
      isPublished
    }
  }
`;

export const UPDATE_LESSON_TASK_MUTATION = gql`
  mutation UpdateLessonTask($input: UpdateLessonTaskInput!) {
    updateLessonTask(input: $input) {
      id
      lessonID
      title
      taskText
      hintText
      answerText
      position
      difficulty
      estimatedMinutes
      isPublished
    }
  }
`;

export const DELETE_LESSON_TASK_MUTATION = gql`
  mutation DeleteLessonTask($id: ID!) {
    deleteLessonTask(id: $id)
  }
`;

export const ALTKLAUSUREN_QUERY = gql`
  query TutorAltklausuren($subjectID: ID!) {
    altklausuren(subjectID: $subjectID) {
      id
      subjectID
      title
      description
      isPublished
      versions {
        id
        altklausurID
        title
        versionDate
        description
        sourceType
        timeLimitSeconds
        questionCount
        isPublished
        isLocked
        quizQuestions {
          id
          altklausurVersionID
          position
          questionText
          questionType
          explanation
          hint
          points
          sourceType
          options {
            id
            questionID
            position
            optionText
            isCorrect
          }
        }
        tasks {
          id
          altklausurVersionID
          title
          taskText
          hintText
          answerText
          position
          difficulty
          estimatedMinutes
          sourceType
          isPublished
        }
      }
    }
  }
`;

export const CREATE_ALTKLAUSUR_MUTATION = gql`
  mutation CreateAltklausur($input: AltklausurInput!) {
    createAltklausur(input: $input) {
      id
      subjectID
      title
      description
      isPublished
    }
  }
`;

export const UPDATE_ALTKLAUSUR_MUTATION = gql`
  mutation UpdateAltklausur($id: ID!, $input: AltklausurInput!) {
    updateAltklausur(id: $id, input: $input) {
      id
      subjectID
      title
      description
      isPublished
    }
  }
`;

export const CREATE_ALTKLAUSUR_VERSION_MUTATION = gql`
  mutation CreateAltklausurVersion($input: AltklausurVersionInput!) {
    createAltklausurVersion(input: $input) {
      id
      altklausurID
      title
      versionDate
      description
      timeLimitSeconds
      isPublished
      isLocked
    }
  }
`;

export const UPDATE_ALTKLAUSUR_VERSION_MUTATION = gql`
  mutation UpdateAltklausurVersion($id: ID!, $input: AltklausurVersionInput!) {
    updateAltklausurVersion(id: $id, input: $input) {
      id
      altklausurID
      title
      versionDate
      description
      timeLimitSeconds
      isPublished
      isLocked
    }
  }
`;

export const CREATE_ALTKLAUSUR_QUIZ_QUESTION_MUTATION = gql`
  mutation CreateAltklausurQuizQuestion($input: CreateAltklausurQuizQuestionInput!) {
    createAltklausurQuizQuestion(input: $input) {
      id
      altklausurVersionID
      position
      questionText
      questionType
      points
      hint
      explanation
      sourceType
    }
  }
`;

export const UPDATE_ALTKLAUSUR_QUIZ_QUESTION_MUTATION = gql`
  mutation UpdateAltklausurQuizQuestion($input: UpdateAltklausurQuizQuestionInput!) {
    updateAltklausurQuizQuestion(input: $input) {
      id
      altklausurVersionID
      position
      questionText
      questionType
      points
      hint
      explanation
      sourceType
    }
  }
`;

export const DELETE_ALTKLAUSUR_QUIZ_QUESTION_MUTATION = gql`
  mutation DeleteAltklausurQuizQuestion($id: ID!) {
    deleteAltklausurQuizQuestion(id: $id)
  }
`;

export const CREATE_ALTKLAUSUR_QUIZ_OPTION_MUTATION = gql`
  mutation CreateAltklausurQuizOption($input: CreateAltklausurQuizOptionInput!) {
    createAltklausurQuizOption(input: $input) {
      id
      questionID
      position
      optionText
      isCorrect
    }
  }
`;

export const UPDATE_ALTKLAUSUR_QUIZ_OPTION_MUTATION = gql`
  mutation UpdateAltklausurQuizOption($input: UpdateAltklausurQuizOptionInput!) {
    updateAltklausurQuizOption(input: $input) {
      id
      questionID
      position
      optionText
      isCorrect
    }
  }
`;

export const DELETE_ALTKLAUSUR_QUIZ_OPTION_MUTATION = gql`
  mutation DeleteAltklausurQuizOption($id: ID!) {
    deleteAltklausurQuizOption(id: $id)
  }
`;

export const CREATE_ALTKLAUSUR_TASK_MUTATION = gql`
  mutation CreateAltklausurTask($input: CreateAltklausurTaskInput!) {
    createAltklausurTask(input: $input) {
      id
      altklausurVersionID
      title
      taskText
      hintText
      answerText
      position
      difficulty
      estimatedMinutes
      isPublished
    }
  }
`;

export const UPDATE_ALTKLAUSUR_TASK_MUTATION = gql`
  mutation UpdateAltklausurTask($input: UpdateAltklausurTaskInput!) {
    updateAltklausurTask(input: $input) {
      id
      altklausurVersionID
      title
      taskText
      hintText
      answerText
      position
      difficulty
      estimatedMinutes
      isPublished
    }
  }
`;

export const DELETE_ALTKLAUSUR_TASK_MUTATION = gql`
  mutation DeleteAltklausurTask($id: ID!) {
    deleteAltklausurTask(id: $id)
  }
`;

/* ---------------- helpers ---------------- */
